import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useOpportunities } from "@/hooks/useOpportunities";
import { usePipelines } from "@/hooks/usePipeline";
import {
  CANONICAL_STATES,
  type AnalyticsSummary,
  type Opportunity,
  type Pipeline,
} from "@sales-ops-amplify/shared";
import {
  computePipelineAnalytics,
  computeWeeklyActivity,
  topCompanies,
  type PipelineAnalytics,
} from "@/lib/pipeline-analytics";
import { getAnalyticsSummary } from "@/api/analytics";
import { cn } from "@/cn";

const STAGE_COLORS: Record<string, string> = {
  Lead: "bg-zinc-500",
  Qualified: "bg-sky-500",
  Discovery: "bg-sky-400",
  Proposal: "bg-indigo-400",
  Demo: "bg-emerald-400",
  Negotiation: "bg-emerald-500",
  Won: "bg-emerald-600",
  Lost: "bg-red-400",
  Passed: "bg-zinc-600",
  Watch: "bg-zinc-600",
};

const STAGE_STROKE: Record<string, string> = {
  Lead: "#71717a",
  Qualified: "#0ea5e9",
  Discovery: "#38bdf8",
  Proposal: "#818cf8",
  Demo: "#34d399",
  Negotiation: "#10b981",
  Won: "#059669",
  Lost: "#f87171",
  Passed: "#52525b",
  Watch: "#a1a1aa",
};

const SOURCE_STROKE = [
  "#0ea5e9",
  "#818cf8",
  "#34d399",
  "#f59e0b",
  "#f87171",
  "#a78bfa",
  "#22d3ee",
  "#94a3b8",
];

const SCORE_LABELS = ["4+", "3-4", "2-3", "1-2", "0-1"] as const;

function clientSummaryFallback(
  jobs: Opportunity[],
  _pipelines: Pipeline[],
  stats: PipelineAnalytics,
): AnalyticsSummary {
  const stageAmounts: Record<string, number> = Object.fromEntries(
    CANONICAL_STATES.map((s) => [s, 0]),
  );
  for (const job of jobs) {
    const stage = job.stage ?? "Qualified";
    const amount = typeof job.amount === "number" ? job.amount : 0;
    stageAmounts[stage] = (stageAmounts[stage] ?? 0) + amount;
  }

  const sourceMap = new Map<string, number>();
  for (const job of jobs) {
    const src = String(job.source || "unknown");
    sourceMap.set(src, (sourceMap.get(src) ?? 0) + 1);
  }

  const monthPrefix = new Date().toISOString().slice(0, 7);
  let wonThisMonth = 0;
  for (const job of jobs) {
    if ((job.stage ?? "") !== "Won") continue;
    const ts = job.updatedAt ?? job.createdAt;
    if (ts?.startsWith(monthPrefix)) {
      wonThisMonth += typeof job.amount === "number" ? job.amount : 0;
    }
  }

  return {
    stageCounts: stats.stageCounts,
    stageAmounts,
    winRate: stats.winRate,
    weightedPipeline: stats.weightedPipeline,
    sourceRoi: [...sourceMap.entries()]
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count),
    forecastVsActual: {
      wonThisMonth,
      pipelineExpected: stats.weightedPipeline,
    },
    icpConversion: { matching: 0, total: jobs.length, rate: "0.0" },
  };
}

function formatCurrency(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4",
        highlight ? "border-brand/30 bg-brand/[0.03]" : "border-border",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function FunnelRow({
  label,
  count,
  total,
  color,
  amount,
  href,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
  amount?: number;
  href?: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const row = (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md px-1 py-0.5",
        href && "hover:bg-muted/40",
      )}
    >
      <div className="flex items-center gap-2" style={{ width: 100 }}>
        <span className={cn("inline-block h-2.5 w-2.5 rounded-full", color)} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex-1">
        <div className="h-3 overflow-hidden rounded-full bg-muted/20">
          <div
            className={cn("h-full rounded-full", color)}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="w-28 text-right text-sm text-muted-foreground">
        {count}
        <span className="text-xs"> ({pct}%)</span>
        {amount != null && amount > 0 && (
          <span className="block text-[10px]">{formatCurrency(amount)}</span>
        )}
      </span>
    </div>
  );
  if (!href || count <= 0) return row;
  return (
    <Link to={href} className="block">
      {row}
    </Link>
  );
}

type DonutSlice = {
  label: string;
  value: number;
  color: string;
  href?: string;
};

function DonutChart({ slices, emptyLabel }: { slices: DonutSlice[]; emptyLabel: string }) {
  const navigate = useNavigate();
  const active = slices.filter((s) => s.value > 0);
  const total = active.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  const size = 148;
  const stroke = 26;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90 shrink-0"
        aria-hidden
      >
        {active.map((slice) => {
          const length = (slice.value / total) * circumference;
          const dashoffset = -offset;
          offset += length;
          return (
            <circle
              key={slice.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={slice.color}
              strokeWidth={stroke}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={dashoffset}
              className={
                slice.href
                  ? "cursor-pointer transition-opacity hover:opacity-80"
                  : undefined
              }
              onClick={
                slice.href
                  ? () => {
                      void navigate(slice.href!);
                    }
                  : undefined
              }
            />
          );
        })}
      </svg>
      <ul className="min-w-0 flex-1 space-y-1.5">
        {active.map((slice) => {
          const pct = Math.round((slice.value / total) * 100);
          const content = (
            <>
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: slice.color }}
              />
              <span className="min-w-0 flex-1 truncate text-foreground">
                {slice.label}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {slice.value} ({pct}%)
              </span>
            </>
          );
          return (
            <li key={slice.label}>
              {slice.href ? (
                <Link
                  to={slice.href}
                  className="flex items-center gap-2 rounded-md text-sm hover:bg-muted/40"
                >
                  {content}
                </Link>
              ) : (
                <div className="flex items-center gap-2 text-sm">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RateCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  const numVal = parseFloat(value);
  const tone =
    numVal >= 30
      ? "text-emerald-400"
      : numVal >= 15
        ? "text-amber-400"
        : "text-red-400";

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn("mt-1 text-3xl font-bold", tone)}>{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

/** Existing pipeline/deal analytics widgets (Apollo Deal analytics dashboard). */
export function DealAnalyticsPanel() {
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } =
    useOpportunities();
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();
  const {
    data: apiSummary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery({
    queryKey: ["analytics", "summary"],
    queryFn: getAnalyticsSummary,
    staleTime: 30_000,
    retry: 1,
  });

  const jobs = jobsData?.opportunities ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];
  const isLoading = jobsLoading || pipelinesLoading || summaryLoading;

  const stats = useMemo(
    () => computePipelineAnalytics(jobs, pipelines),
    [jobs, pipelines],
  );

  const summary = useMemo((): AnalyticsSummary => {
    if (apiSummary && !summaryError) return apiSummary;
    return clientSummaryFallback(jobs, pipelines, stats);
  }, [apiSummary, summaryError, jobs, pipelines, stats]);

  const weekly = useMemo(() => computeWeeklyActivity(jobs), [jobs]);
  const companies = useMemo(() => topCompanies(jobs), [jobs]);
  const maxScoreBucket = Math.max(...stats.scoreBuckets, 1);
  const maxWeekly = Math.max(...weekly.values, 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          <p className="text-sm text-muted-foreground">Loading deal analytics…</p>
        </div>
      </div>
    );
  }

  if (jobsError) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">Failed to load analytics data.</p>
      </div>
    );
  }

  const stageCounts = summary.stageCounts ?? stats.stageCounts;
  const funnelTotal =
    stats.inPipeline ||
    Object.values(stageCounts).reduce((a, b) => a + b, 0);

  const stageDonut: DonutSlice[] = CANONICAL_STATES.map((stage) => ({
    label: stage === "Watch" ? "Watching" : stage,
    value: stageCounts[stage] ?? 0,
    color: STAGE_STROKE[stage] ?? "#71717a",
    href: `/deals?stage=${encodeURIComponent(stage)}`,
  }));

  const sourceDonut: DonutSlice[] = summary.sourceRoi.slice(0, 8).map(
    ({ source, count }, i) => ({
      label: source,
      value: count,
      color: SOURCE_STROKE[i % SOURCE_STROKE.length],
      href:
        source && source !== "unknown"
          ? `/deals?q=${encodeURIComponent(source)}`
          : "/deals",
    }),
  );

  return (
    <div className="space-y-8">
      {summaryError ? (
        <p className="text-xs text-muted-foreground">Using client fallback</p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <StatCard label="Total Opportunities" value={String(stats.totalOpportunities)} />
        <StatCard label="In Pipeline" value={String(stats.inPipeline)} />
        <StatCard label="Active" value={String(stats.active)} />
        <StatCard label="Demos" value={String(stats.demos)} />
        <StatCard label="Won" value={String(stats.won)} />
        <StatCard
          label="Unprocessed"
          value={String(stats.unprocessed)}
          highlight={stats.unprocessed > 0}
        />
        <StatCard label="Avg. Score" value={stats.avgScore} />
        <StatCard
          label="Weighted pipeline"
          value={formatCurrency(summary.weightedPipeline)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Win rate" value={`${summary.winRate}%`} />
        <StatCard
          label="Won this month"
          value={formatCurrency(summary.forecastVsActual.wonThisMonth)}
        />
        <StatCard
          label="Pipeline expected"
          value={formatCurrency(summary.forecastVsActual.pipelineExpected)}
        />
        <StatCard
          label="ICP match"
          value={`${summary.icpConversion.matching}/${summary.icpConversion.total} (${summary.icpConversion.rate}%)`}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Stage mix
          </h2>
          <DonutChart slices={stageDonut} emptyLabel="No stage data yet." />
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Source mix
          </h2>
          <DonutChart slices={sourceDonut} emptyLabel="No source data yet." />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Pipeline Funnel
          </h2>
          <div className="space-y-3">
            {CANONICAL_STATES.map((stage) => (
              <FunnelRow
                key={stage}
                label={stage === "Watch" ? "Watching" : stage}
                count={stageCounts[stage] ?? 0}
                amount={summary.stageAmounts?.[stage]}
                total={funnelTotal}
                color={STAGE_COLORS[stage] ?? "bg-zinc-500"}
                href={`/deals?stage=${encodeURIComponent(stage)}`}
              />
            ))}
            {stats.unprocessed > 0 && (
              <FunnelRow
                label="Pending"
                count={stats.unprocessed}
                total={stats.totalOpportunities}
                color="bg-amber-500"
                href="/deals"
              />
            )}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Source breakdown
          </h2>
          {summary.sourceRoi.length === 0 ? (
            <p className="text-sm text-muted-foreground">No source data yet.</p>
          ) : (
            <div className="space-y-3">
              {summary.sourceRoi.slice(0, 8).map(({ source, count }) => {
                const href =
                  source && source !== "unknown"
                    ? `/deals?q=${encodeURIComponent(source)}`
                    : "/deals";
                return (
                  <Link
                    key={source}
                    to={href}
                    className="flex items-center gap-3 rounded-md px-1 py-0.5 hover:bg-muted/40"
                  >
                    <span className="flex-1 truncate text-sm text-foreground">
                      {source}
                    </span>
                    <span className="text-xs text-muted-foreground">{count}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Score Distribution
          </h2>
          <div className="space-y-2.5">
            {SCORE_LABELS.map((label, i) => {
              const idx = 4 - i;
              const count = stats.scoreBuckets[idx];
              const pct = Math.round((count / maxScoreBucket) * 100);
              return (
                <div key={label} className="flex items-center gap-3">
                  <span className="w-8 text-right text-xs text-muted-foreground">
                    {label}
                  </span>
                  <div className="flex-1">
                    <div className="h-4 overflow-hidden rounded-full bg-muted/20">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          idx >= 4
                            ? "bg-emerald-500"
                            : idx >= 3
                              ? "bg-amber-500"
                              : "bg-red-500",
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="w-6 text-right text-xs text-muted-foreground">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Conversion rates
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <RateCard
              label="Proposal Rate"
              value={`${stats.proposalRate}%`}
              description="Proposed / Qualified"
            />
            <RateCard
              label="Response Rate"
              value={`${stats.responseRate}%`}
              description="Engaged / Proposed"
            />
            <RateCard
              label="Demo Rate"
              value={`${stats.demoRate}%`}
              description="Demos / Proposed"
            />
            <RateCard
              label="Win Rate"
              value={`${summary.winRate}%`}
              description="Won / Proposed"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Weekly Activity
          </h2>
          {weekly.labels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No activity data available yet.
            </p>
          ) : (
            <div className="flex items-end gap-2" style={{ height: 120 }}>
              {weekly.labels.map((label, i) => {
                const value = weekly.values[i] ?? 0;
                const pct = Math.round((value / maxWeekly) * 100);
                return (
                  <div
                    key={label}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <span className="text-xs text-muted-foreground">{value}</span>
                    <div
                      className="w-full rounded-sm bg-brand transition-all"
                      style={{
                        height: `${Math.max(pct, 4)}%`,
                        minHeight: 4,
                      }}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Top Companies
          </h2>
          {companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No pipeline opportunities yet.
            </p>
          ) : (
            <div className="space-y-3">
              {companies.map(({ company, count }) => (
                <div key={company} className="flex items-center gap-3">
                  <span className="flex-1 truncate text-sm text-foreground">
                    {company}
                  </span>
                  <div className="w-24">
                    <div className="h-2 overflow-hidden rounded-full bg-muted/20">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{
                          width: `${Math.round((count / companies[0].count) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="w-6 text-right text-xs text-muted-foreground">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
